'use client'

import { cn } from '@brew/ui/cn'
import { useClerk, useUser } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import type { ReactNode } from 'react'
import { MarketingContainer } from './marketing-container'
import {
  MARKETING_GUTTER_X,
  MARKETING_SECTION_HEADING,
  MARKETING_SECTION_Y,
} from './marketing-layout'
import { ProminentCtaButton } from './prominent-cta-button'
import { SectionTopDivider } from './section-divider'

export type FinalCtaProps = {
  heading?: ReactNode
  subheading?: ReactNode
  ctaLabel?: string
  actions?: ReactNode
  onCta?: () => void
  className?: string
  /** Optional decorative element rendered below the copy (e.g. a logo). */
  visual?: ReactNode
}

export function FinalCta({
  heading = 'Start sending with Brew.',
  subheading = 'No credit card. Free credits to try.',
  ctaLabel = 'Get started',
  actions,
  onCta,
  className,
  visual,
}: FinalCtaProps) {
  const { openSignUp } = useClerk()
  const { isSignedIn } = useUser()
  const router = useRouter()

  const handleClick = () => {
    if (onCta) {
      onCta()
      return
    }
    if (isSignedIn) {
      router.push('/home')
      return
    }
    openSignUp()
  }

  return (
    <section
      className={cn(
        'relative w-full overflow-hidden',
        MARKETING_GUTTER_X,
        MARKETING_SECTION_Y,
        className
      )}
    >
      <SectionTopDivider />
      <MarketingContainer
        size="prose"
        className="relative z-10 flex flex-col items-center gap-6 text-center"
      >
        <h2 className={cn(MARKETING_SECTION_HEADING, 'text-foreground')}>
          {heading}
        </h2>
        {actions ?? (
          <ProminentCtaButton onClick={handleClick}>
            {ctaLabel}
          </ProminentCtaButton>
        )}
        {subheading ? (
          <p className="text-muted-foreground text-sm">{subheading}</p>
        ) : null}
        {visual}
      </MarketingContainer>
    </section>
  )
}
